import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { LuPaperclip, LuSend, LuX } from "react-icons/lu";
import { openSupportConversation } from "../../Services/supportService";
import { useSupportChat } from "../../hooks/useSupportChat";

const MessageBubble = ({ message, isMine }) => {
  const isImage = message.attachment_type?.startsWith("image/");
  return (
    <div className={`flex mb-3 ${isMine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2 ${
          isMine
            ? "bg-[#425F80] text-white rounded-br-sm"
            : "bg-gray-100 text-gray-800 rounded-bl-sm"
        }`}
      >
        {message.sender_type === "system" && (
          <p className="text-xs italic opacity-70 mb-1">System</p>
        )}
        {message.attachment_url &&
          (isImage ? (
            <a href={message.attachment_url} target="_blank" rel="noreferrer">
              <img
                src={message.attachment_url}
                alt={message.attachment_name || "attachment"}
                className="rounded-lg mb-1 max-h-52 object-cover"
              />
            </a>
          ) : (
            <a
              href={message.attachment_url}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center gap-2 underline text-sm mb-1 ${
                isMine ? "text-white" : "text-blue-600"
              }`}
            >
              <LuPaperclip size={14} />
              {message.attachment_name || "Attachment"}
            </a>
          ))}
        {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
        <p className={`text-[10px] mt-1 ${isMine ? "text-white/70" : "text-gray-400"}`}>
          {moment(message.created_at).format("h:mm A")}
        </p>
      </div>
    </div>
  );
};

const SupportChat = () => {
  const [conversationId, setConversationId] = useState(null);
  const [initError, setInitError] = useState(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const { messages, loading, sending, send } = useSupportChat(conversationId);

  // Open / reuse a conversation on mount.
  useEffect(() => {
    openSupportConversation()
      .then((conv) => setConversationId(conv?.id))
      .catch((err) =>
        setInitError(err?.response?.data?.error?.message || "Unable to start chat.")
      );
  }, []);

  // Auto scroll on new messages.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !file) || sending) return;
    const body = text;
    const attachment = file;
    setText("");
    setFile(null);
    try {
      await send({ body, file: attachment });
    } catch (err) {
      // restore on failure
      setText(body);
      setFile(attachment);
    }
  };

  return (
    <div className="page-container p-4">
      <div className="flex flex-col h-[calc(100vh-140px)] max-w-3xl w-full mx-auto bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Customer Support</h2>
            <p className="text-xs text-green-600 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              We typically reply within a few minutes
            </p>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4">
          {initError ? (
            <p className="text-center text-red-500 mt-8">{initError}</p>
          ) : loading ? (
            <p className="text-center text-gray-400 mt-8">Loading conversation…</p>
          ) : messages.length === 0 ? (
            <p className="text-center text-gray-400 mt-8">
              Start the conversation — ask us anything.
            </p>
          ) : (
            messages.map((m) => (
              <MessageBubble key={m.id} message={m} isMine={m.sender_type === "advisor"} />
            ))
          )}
        </div>

        {/* Composer */}
        <form onSubmit={handleSend} className="border-t border-gray-100 px-4 py-3">
          {file && (
            <div className="flex items-center gap-2 mb-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-1.5 w-fit">
              <LuPaperclip size={14} />
              <span className="truncate max-w-[200px]">{file.name}</span>
              <button type="button" onClick={() => setFile(null)}>
                <LuX size={14} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-500 hover:text-[#425F80]"
              title="Attach file"
            >
              <LuPaperclip size={20} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your message…"
              disabled={!conversationId || !!initError}
              className="flex-1 border border-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#425F80]/30"
            />
            <button
              type="submit"
              disabled={sending || (!text.trim() && !file)}
              className="p-2.5 rounded-full bg-[#425F80] text-white disabled:opacity-40"
              title="Send"
            >
              <LuSend size={18} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportChat;
